import { useAuth } from "@clerk/clerk-react";
import { useMemo } from "react";
import { previewClient } from "./previewApi.ts";

// Same-origin in production (app.stift.sh/api/*). Overridable for local dev or
// split-origin setups via VITE_API_BASE.
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

// ---- API contract (assumed; implemented by the control-plane Worker) --------

export interface Session {
  id: string;
  agent: string;
  project: string;
  host: string;
  title: string;
  size: number; // bytes
  updated_at: string; // RFC3339
}

export interface Token {
  id: string;
  name: string;
  admin: boolean;
  created_at: string; // RFC3339
  last_used: string | null; // RFC3339 or null if never used
}

export interface CreatedToken {
  id: string;
  name: string;
  token: string; // raw "stf_..." secret — shown exactly once
}

export interface Usage {
  plan: string;
  bytes: number;
  sessions: number;
  // null = unlimited (no cap for that dimension).
  max_bytes: number | null;
  max_sessions: number | null;
}

// Skills (config-unit bundles) — see control-plane/src/routes/skills.ts.

export interface SkillMeta {
  path: string;
  name: string;
  description: string;
}

/** One row of GET /api/skills (the bundle_index mirror: HEAD of each unit). */
export interface SkillSummary {
  scope: "user" | "project" | "org" | string;
  agent: string;
  project: string; // abs path for scope=project, else ""
  name: string; // unit name, e.g. "skills/deploy"
  version: number;
  files: number;
  size: number; // bytes
  skills: SkillMeta[];
  author: string;
  host: string;
  updated_at: string | null; // RFC3339
}

export interface BundleFile {
  path: string;
  sha256: string;
  size: number;
  mode: number;
}

/** A full manifest (engine api.Bundle). `head` is present on single GETs. */
export interface Bundle {
  scope: string;
  agent: string;
  project?: string;
  name: string;
  version: number;
  parent: number;
  host: string;
  author: string;
  created: string; // RFC3339
  files: BundleFile[];
  skills: SkillMeta[];
  head?: number;
}

export interface SkillKey {
  scope: string;
  agent: string;
  name: string;
  project?: string;
}

export interface CheckoutResponse {
  url: string;
}

export type GetToken = () => Promise<string | null>;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ---- Core client ------------------------------------------------------------

/**
 * A thin typed fetch client bound to a Clerk getToken() function. The Clerk
 * session token is attached as a Bearer header; the active organization is
 * encoded in that token's claims, so the control plane scopes every request to
 * the current org/tenant.
 */
export class ApiClient {
  constructor(private getToken: GetToken) {}

  private async request<T>(
    path: string,
    init: RequestInit = {},
    parse: "json" | "none" = "json",
  ): Promise<T> {
    const token = await this.getToken();
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

    if (!res.ok) {
      let body: unknown;
      let message = `${res.status} ${res.statusText}`;
      try {
        body = await res.json();
        if (body && typeof body === "object" && "error" in body) {
          message = String((body as { error: unknown }).error);
        }
      } catch {
        // non-JSON error body; keep the status-line message
      }
      throw new ApiError(res.status, message, body);
    }

    if (parse === "none") return undefined as T;
    return (await res.json()) as T;
  }

  // Sessions ----------------------------------------------------------------

  listSessions(signal?: AbortSignal): Promise<Session[]> {
    return this.request<Session[]>("/api/sessions", { signal });
  }

  /**
   * Returns a temporary object URL for the session's tar.gz archive. The caller
   * is responsible for revoking it with URL.revokeObjectURL after use.
   */
  async sessionArchiveUrl(id: string, signal?: AbortSignal): Promise<string> {
    const token = await this.getToken();
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}/api/sessions/${encodeURIComponent(id)}/archive`,
      { headers, signal },
    );
    if (!res.ok) {
      throw new ApiError(res.status, `${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  // Tokens ------------------------------------------------------------------

  listTokens(signal?: AbortSignal): Promise<Token[]> {
    return this.request<Token[]>("/api/tokens", { signal });
  }

  createToken(name: string): Promise<CreatedToken> {
    return this.request<CreatedToken>("/api/tokens", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  revokeToken(id: string): Promise<void> {
    return this.request<void>(
      `/api/tokens/${encodeURIComponent(id)}`,
      { method: "DELETE" },
      "none",
    );
  }

  // Skills ------------------------------------------------------------------

  listSkills(
    filter: { scope?: string; agent?: string } = {},
    signal?: AbortSignal,
  ): Promise<SkillSummary[]> {
    const q = new URLSearchParams();
    if (filter.scope) q.set("scope", filter.scope);
    if (filter.agent) q.set("agent", filter.agent);
    const qs = q.toString();
    return this.request<SkillSummary[]>(`/api/skills${qs ? `?${qs}` : ""}`, {
      signal,
    });
  }

  getSkill(key: SkillKey, version?: number, signal?: AbortSignal): Promise<Bundle> {
    return this.request<Bundle>(
      skillPath(key, "", version != null ? { version: String(version) } : {}),
      { signal },
    );
  }

  getSkillHistory(key: SkillKey, signal?: AbortSignal): Promise<Bundle[]> {
    return this.request<Bundle[]>(skillPath(key, "", { history: "1" }), {
      signal,
    });
  }

  /** Raw file content at `path` (HEAD unless `version`). Text for *.md. */
  async getSkillFile(
    key: SkillKey,
    path: string,
    version?: number,
    signal?: AbortSignal,
  ): Promise<string> {
    const token = await this.getToken();
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(
      `${API_BASE}${skillPath(key, "/file", {
        path,
        ...(version != null ? { version: String(version) } : {}),
      })}`,
      { headers, signal },
    );
    if (!res.ok) {
      throw new ApiError(res.status, `${res.status} ${res.statusText}`);
    }
    return res.text();
  }

  /**
   * Save a new version of one markdown file. `parent` is the version the edit
   * was based on; a 409 ApiError with body `{error:"stale", head}` means
   * someone published in between.
   */
  saveSkillFile(
    key: SkillKey,
    path: string,
    content: string,
    parent: number,
  ): Promise<Bundle> {
    return this.request<Bundle>(skillPath(key), {
      method: "PUT",
      body: JSON.stringify({ path, content, parent }),
    });
  }

  rollbackSkill(key: SkillKey, version: number): Promise<Bundle> {
    return this.request<Bundle>(skillPath(key, "/rollback"), {
      method: "POST",
      body: JSON.stringify({ version }),
    });
  }

  deleteSkill(key: SkillKey): Promise<void> {
    return this.request<void>(skillPath(key), { method: "DELETE" }, "none");
  }

  // Billing / usage ---------------------------------------------------------

  getUsage(signal?: AbortSignal): Promise<Usage> {
    return this.request<Usage>("/api/usage", { signal });
  }

  // Single per-seat paid plan; the control plane bills for the org's seats and
  // owns the price id, so the frontend sends no parameters.
  startCheckout(): Promise<CheckoutResponse> {
    return this.request<CheckoutResponse>("/api/billing/checkout", {
      method: "POST",
    });
  }

  openBillingPortal(): Promise<CheckoutResponse> {
    return this.request<CheckoutResponse>("/api/billing/portal", {
      method: "POST",
    });
  }
}

/** /api/skills/{scope}/{agent}/{name...}{suffix}?project=&... */
function skillPath(
  key: SkillKey,
  suffix = "",
  query: Record<string, string> = {},
): string {
  const q = new URLSearchParams(query);
  if (key.project) q.set("project", key.project);
  const qs = q.toString();
  const name = key.name.split("/").map(encodeURIComponent).join("/");
  return `/api/skills/${encodeURIComponent(key.scope)}/${encodeURIComponent(
    key.agent,
  )}/${name}${suffix}${qs ? `?${qs}` : ""}`;
}

// Build-time constant: local design preview with mock data, no Clerk/control
// plane (production Clerk keys refuse to run on localhost). Off unless the
// VITE_PREVIEW=1 flag is set, so normal builds are unaffected.
const PREVIEW = import.meta.env.VITE_PREVIEW === "1";

/** Hook that returns a memoized ApiClient bound to the current Clerk session. */
export function useApi(): ApiClient {
  // PREVIEW is a compile-time constant, so the conditional hook below never
  // changes order between renders — safe despite the rules-of-hooks shape.
  if (PREVIEW) return previewClient;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getToken } = useAuth();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(() => new ApiClient(() => getToken()), [getToken]);
}
